import { SearchInput } from '@components/search/SearchInput'
import {
  FilterField,
  RentRangeInput,
  TableFilterPanel
} from '@components/table/filters'
import { useLocations } from '@services/common/useLocation'
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Combobox, ComboboxOption } from '@/components/ui/combobox'

const FILTER_KEYS = ['status', 'rent_min', 'rent_max', 'location'] as const

const STATUSES = [
  'CREATED',
  'INTERESTED',
  'ACCEPTED',
  'RESERVED',
  'ONGOING',
  'NOTICE_PERIOD',
  'TERMINATED',
  'REJECTED',
  'CANCELED'
] as const

interface Filters {
  status?: string
  rent_min?: number
  rent_max?: number
  location?: string
}

type LocationNode = { title: string; value: string; children?: LocationNode[] }

function flattenLocations(
  nodes: LocationNode[] | undefined,
  prefix: string[] = []
): ComboboxOption[] {
  const out: ComboboxOption[] = []
  for (const node of nodes ?? []) {
    const path = [...prefix, node.title]
    out.push({ value: node.value, label: path.join(' › ') })
    if (node.children?.length) out.push(...flattenLocations(node.children, path))
  }
  return out
}

function parseFiltersFromParams(searchParams: URLSearchParams): Filters {
  return {
    status: searchParams.get('status') || undefined,
    rent_min: searchParams.get('rent_min')
      ? Number(searchParams.get('rent_min'))
      : undefined,
    rent_max: searchParams.get('rent_max')
      ? Number(searchParams.get('rent_max'))
      : undefined,
    location: searchParams.get('location') || undefined
  }
}

interface Props {
  actionBar?: ReactElement
}

export function OffersTableFilters({ actionBar }: Props) {
  const { t } = useTranslation('offers')
  const { t: tLayout } = useTranslation('layout')
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: locations, isLoading: locationsLoading } = useLocations()

  const [filtersUI, setFiltersUI] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )
  const [filters, setFilters] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )

  const areEqual = (a: Filters, b: Filters) =>
    JSON.stringify(a) === JSON.stringify(b)

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (areEqual(filtersUI, filters)) return
      setFilters(filtersUI)

      const params = new URLSearchParams(searchParams)
      FILTER_KEYS.forEach((key) => params.delete(key))
      params.delete('offset')

      if (filtersUI.status !== undefined) params.set('status', filtersUI.status)
      if (filtersUI.rent_min !== undefined)
        params.set('rent_min', String(filtersUI.rent_min))
      if (filtersUI.rent_max !== undefined)
        params.set('rent_max', String(filtersUI.rent_max))
      if (filtersUI.location) params.set('location', filtersUI.location)

      setSearchParams(params)
    }, 400)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersUI])

  const hasActiveFilters = useMemo(
    () =>
      filtersUI.status !== undefined ||
      filtersUI.rent_min !== undefined ||
      filtersUI.rent_max !== undefined ||
      filtersUI.location !== undefined,
    [filtersUI]
  )

  const handleClear = useCallback(() => setFiltersUI({}), [])

  const statusOptions: ComboboxOption[] = useMemo(
    () => STATUSES.map((s) => ({ value: s, label: t(`statuses.${s}`) })),
    [t]
  )

  const locationOptions = useMemo(
    () => flattenLocations(locations as LocationNode[] | undefined),
    [locations]
  )

  const numberOr = (v: string) => (v === '' ? undefined : Number(v))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput />
        {actionBar}
      </div>

      <TableFilterPanel
        title={t('filters.title')}
        clearLabel={t('filters.clear')}
        hasActiveFilters={hasActiveFilters}
        onClear={handleClear}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FilterField label={t('sections.status')}>
            <Combobox
              clearable
              searchable={false}
              placeholder={t('filters.status')}
              value={filtersUI.status}
              onChange={(v) => setFiltersUI({ ...filtersUI, status: v })}
              options={statusOptions}
            />
          </FilterField>

          <FilterField label={t('sections.location')}>
            <Combobox
              clearable
              placeholder={tLayout('common.select_location')}
              value={filtersUI.location}
              loading={locationsLoading}
              onChange={(v) => setFiltersUI({ ...filtersUI, location: v })}
              options={locationOptions}
            />
          </FilterField>

          <FilterField label={t('filters.rentRange')}>
            <RentRangeInput
              minValue={filtersUI.rent_min}
              maxValue={filtersUI.rent_max}
              onMinChange={(raw) =>
                setFiltersUI({ ...filtersUI, rent_min: numberOr(raw) })
              }
              onMaxChange={(raw) =>
                setFiltersUI({ ...filtersUI, rent_max: numberOr(raw) })
              }
              fromPlaceholder={t('filters.from')}
              toPlaceholder={t('filters.to')}
            />
          </FilterField>
        </div>
      </TableFilterPanel>
    </div>
  )
}
